// ui/alert.js
import { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import "./Alert.css";
import { useTranslation } from 'react-i18next';

const Icons = {
  warning: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="11" opacity="0.12" />
      <path d="M12 7v7" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="17" r="1.5" />
    </svg>
  ),
  success: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="11" opacity="0.12" />
      <path d="M7 12.5l3.5 3.5L17 9" strokeWidth="2" strokeLinecap="round" fill="none" />
    </svg>
  ),
  error: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="11" opacity="0.12" />
      <path d="M8 8l8 8M16 8l-8 8" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  info: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="11" opacity="0.12" />
      <path d="M12 10v7M12 7h.01" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  question: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="11" opacity="0.12" />
      <path d="M9.5 9a2.5 2.5 0 115 0c0 2-2.5 2-2.5 4" strokeWidth="2" strokeLinecap="round" fill="none" />
      <circle cx="12" cy="17" r="1" />
    </svg>
  ),
};

function AlertDialog({ options, resolve }) {
  const { t } = useTranslation('global');
  const {
    title,
        // eslint-disable-next-line
    text,
            // eslint-disable-next-line
    html,
    icon = "warning",
    showCancelButton,
    confirmButtonText = t('confirm'),
    cancelButtonText = t('cancel'),
    allowOutsideClick = true,
    allowEscapeKey = true,
    reverseButtons = false,
    preConfirm,
    showLoaderOnConfirm = true,
  } = options || {};

  const overlayRef = useRef(null);
  const confirmRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState(null);

  useEffect(() => {
    confirmRef.current && confirmRef.current.focus();
    const onKey = (e) => {
      if (e.key === "Escape" && allowEscapeKey && !loading) {
        resolve({ isConfirmed: false, isDismissed: true });
      }
      if (e.key === "Enter" && !loading) {
        handleConfirm();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const handleOutside = (e) => {
    if (!allowOutsideClick || loading) return;
    if (e.target === overlayRef.current) {
      resolve({ isConfirmed: false, isDismissed: true });
    }
  };

  const handleConfirm = async () => {
    if (!preConfirm) {
      resolve({ isConfirmed: true, isDismissed: false });
      return;
    }
    try {
      setErrorText(null);
      if (showLoaderOnConfirm) setLoading(true);
      const v = await preConfirm();
      resolve({ isConfirmed: true, isDismissed: false, value: v });
    } catch (err) {
      const msg =
        typeof err === "string" ? err : (err && err.message) || "Something went wrong.";
      setErrorText(msg);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (loading) return;
    resolve({ isConfirmed: false, isDismissed: true });
  };

  return (
    <div className="ua-overlay" ref={overlayRef} onMouseDown={handleOutside}>
      <div className="ua-dialog" role="dialog" aria-modal="true" aria-labelledby="ua-title">
        <div className={`ua-icon ua-icon--${icon}`}>{Icons[icon]}</div>

        {title ? (
          <h3 id="ua-title" className="ua-title">
            {title}
          </h3>
        ) : null}

{/* Body: render HTML if provided, else text */}
        {options.html ? (
          <div
            className="ua-body"
            // Make sure you only pass trusted strings here!
            dangerouslySetInnerHTML={{ __html: options.html }}
          />
        ) : options.text ? (
          <div className="ua-body">{options.text}</div>
        ) : null}

        {errorText ? <div className="ua-inline-error">{errorText}</div> : null}

        <div className={`ua-actions ${reverseButtons ? "is-reversed" : ""}`}>
          {showCancelButton ? (
            <button className="ua-btn ua-btn--ghost" onClick={handleCancel} disabled={loading}>
              {cancelButtonText}
            </button>
          ) : null}
          <button
            ref={confirmRef}
            className="ua-btn ua-btn--primary"
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? <span className="ua-spinner" aria-hidden="true" /> : null}
            {confirmButtonText}
          </button>
        </div>
      </div>
    </div>
  );
}

const UltraAlert = {
  fire(opts) {
    const host = document.createElement("div");
    document.body.appendChild(host);
    const root = createRoot(host);

    return new Promise((resolve) => {
      const finish = (res) => {
        root.unmount();
        host.remove();
        resolve(res);
      };
      root.render(<AlertDialog options={opts || {}} resolve={finish} />);
    });
  },
};

export default UltraAlert;
export { UltraAlert };
